import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ChurchCouponsDialog } from '@/components/church-coupons-dialog';
import {
  PlatformSessionRequestNotice,
  usePlatformSessionRequests,
} from '@/components/platform-session-request-notice';
import { ChurchHistoryDialog } from '@/components/church-history-dialog';
import { DetailField } from '@/components/shared/detail-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/shared/form-field';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { OPEN_SUPPORT_TICKET_STATUSES } from '@/lib/support-ticket-types';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';
import { formatDate, isoToCalendarDate } from '@/utils/format';

const PLANS = ['trial', 'community', 'starter', 'essential', 'professional', 'enterprise'] as const;
const MANUAL_PAID_PLANS = ['essential', 'professional', 'enterprise'] as const;
const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'grace',
  'suspended',
  'cancelled',
  'expired',
] as const;

type Plan = (typeof PLANS)[number];
type ManualPaidPlan = (typeof MANUAL_PAID_PLANS)[number];
type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

const isManualPaidPlan = (plan: string): plan is ManualPaidPlan =>
  (MANUAL_PAID_PLANS as readonly string[]).includes(plan);

const isTrialingSubscription = (subscription: {
  plan: Plan;
  status: SubscriptionStatus;
}): boolean => subscription.status === 'trialing' || subscription.plan === 'trial';

interface PlatformChurchDetail {
  church: {
    id: string;
    name: string;
    campusName: string;
    status: 'active' | 'suspended' | 'banned' | 'deactivated';
    contactEmail: string | null;
    country: string | null;
    parentChurchId: string | null;
    defaultTimezone: string;
    createdAt: string;
    updatedAt: string;
  };
  subscription: {
    plan: Plan;
    status: SubscriptionStatus;
    includedSeats: number;
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
  };
  payments: {
    suspended: boolean;
    suspendedAt: string | null;
    suspendedReason: string | null;
  };
}

export const ChurchDetailDialog = ({
  churchId,
  open,
  onOpenChange,
}: {
  churchId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const isAdmin = useAuthStore((state) => state.user?.platformRole) === 'platform_admin';
  const sessionRequests = usePlatformSessionRequests({
    churchId: churchId ?? undefined,
    enabled: open && isAdmin,
  });
  const currentSession = sessionRequests.data?.data[0];

  const [statusReason, setStatusReason] = useState('');
  const [subscriptionReason, setSubscriptionReason] = useState('');
  const [plan, setPlan] = useState<Plan>('trial');
  const [trialPlan, setTrialPlan] = useState<ManualPaidPlan | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('active');
  const [includedSeats, setIncludedSeats] = useState('5');
  const [trialEndsOn, setTrialEndsOn] = useState<string | null>(null);
  const [periodEndsOn, setPeriodEndsOn] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'suspended' | null>(null);
  const [pendingPayments, setPendingPayments] = useState<'suspend' | 'resume' | null>(null);
  const [paymentsReason, setPaymentsReason] = useState('');
  const [supportVisitOpen, setSupportVisitOpen] = useState(false);
  const [supportVisitReason, setSupportVisitReason] = useState('');
  const [supportVisitLink, setSupportVisitLink] = useState<string | null>(null);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [couponsOpen, setCouponsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['platform-church', churchId],
    queryFn: () => api.get<PlatformChurchDetail>(`/platform/churches/${churchId}`),
    enabled: open && Boolean(churchId),
  });

  const openTickets = useQuery({
    queryKey: ['platform-church-open-tickets', churchId],
    queryFn: () =>
      api.get<{ data: unknown[]; page: OffsetPage }>('/platform/support/tickets', {
        query: {
          page: 1,
          pageSize: 1,
          churchId: churchId!,
          status: OPEN_SUPPORT_TICKET_STATUSES.join(','),
        },
      }),
    enabled: open && Boolean(churchId),
  });

  const timezone = detail.data?.church.defaultTimezone || 'UTC';

  useEffect(() => {
    setCouponsOpen(false);
    setHistoryOpen(false);
    setSupportVisitOpen(false);
    setSupportVisitReason('');
    setSupportVisitLink(null);
    setPendingPayments(null);
    setPaymentsReason('');
  }, [churchId]);

  useEffect(() => {
    if (!detail.data) return;
    const { subscription } = detail.data;
    const onTrial = isTrialingSubscription(subscription);
    setPlan(onTrial ? 'trial' : subscription.plan);
    setTrialPlan(onTrial && isManualPaidPlan(subscription.plan) ? subscription.plan : null);
    setSubscriptionStatus(onTrial ? 'trialing' : subscription.status);
    setIncludedSeats(String(subscription.includedSeats));
    setTrialEndsOn(
      subscription.trialEndsAt ? isoToCalendarDate(subscription.trialEndsAt, timezone) : null,
    );
    setPeriodEndsOn(
      subscription.currentPeriodEnd
        ? isoToCalendarDate(subscription.currentPeriodEnd, timezone)
        : null,
    );
    setSubscriptionReason('');
    setStatusReason('');
    setPendingStatus(null);
  }, [detail.data, timezone, subscriptionOpen]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['platform-churches'] });
    void queryClient.invalidateQueries({ queryKey: ['platform-stats'] });
    void queryClient.invalidateQueries({ queryKey: ['platform-dashboard'] });
    if (churchId) {
      void queryClient.invalidateQueries({ queryKey: ['platform-church', churchId] });
      void queryClient.invalidateQueries({ queryKey: ['platform-church-history', churchId] });
    }
  };

  const updateStatus = useMutation({
    mutationFn: (input: { status: 'active' | 'suspended'; reason: string }) =>
      api.patch(`/platform/churches/${churchId}/status`, input),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.status === 'suspended' ? t('churches.suspended') : t('churches.reactivated'),
      );
      setPendingStatus(null);
      setStatusReason('');
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const updateSubscription = useMutation({
    mutationFn: (input: {
      plan: Plan;
      status: SubscriptionStatus;
      includedSeats: number;
      trialPlan?: ManualPaidPlan;
      trialEndsAt?: string | null;
      currentPeriodEnd?: string | null;
      reason: string;
    }) => {
      const body: Record<string, unknown> = {
        plan: input.plan,
        status: input.plan === 'trial' ? 'trialing' : input.status,
        includedSeats: input.includedSeats,
        reason: input.reason,
        trialEndsAt: input.plan === 'trial' ? input.trialEndsAt : null,
      };
      if (input.plan === 'trial') {
        body.trialPlan = input.trialPlan;
      } else {
        body.currentPeriodEnd = isManualPaidPlan(input.plan) ? input.currentPeriodEnd : null;
      }
      return api.patch(`/platform/churches/${churchId}/subscription`, body);
    },
    onSuccess: () => {
      toast.success(t('churches.subscriptionSaved'));
      setSubscriptionReason('');
      setSubscriptionOpen(false);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const updatePayments = useMutation({
    mutationFn: (input: { suspended: boolean; reason: string }) =>
      api.patch(`/platform/churches/${churchId}/payments`, input),
    onSuccess: (_data, variables) => {
      toast.success(variables.suspended ? t('churches.givingPaused') : t('churches.givingResumed'));
      setPendingPayments(null);
      setPaymentsReason('');
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const startSupportVisit = useMutation({
    mutationFn: (reason: string) =>
      api.post(`/platform/churches/${churchId}/support-access`, { reason }),
    onSuccess: async () => {
      setSupportVisitReason('');
      setSupportVisitOpen(false);
      toast.success(t('churches.supportVisitStarted'));
      invalidate();
      await queryClient.invalidateQueries({ queryKey: ['platform-support-access'] });
      await queryClient.invalidateQueries({ queryKey: ['platform-support-session-requests'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const church = detail.data?.church;
  const subscription = detail.data?.subscription;
  const payments = detail.data?.payments;

  const planOptions = useMemo(
    () => PLANS.map((value) => ({ value, label: t(`billing.plans.${value}`) })),
    [t],
  );

  const trialPlanOptions = useMemo(
    () => MANUAL_PAID_PLANS.map((value) => ({ value, label: t(`billing.plans.${value}`) })),
    [t],
  );

  const subscriptionStatusOptions = useMemo(
    () =>
      SUBSCRIPTION_STATUSES.map((value) => ({
        value,
        label: t(`billing.statuses.${value}`),
      })),
    [t],
  );

  const calendarLabels = useMemo(
    () => ({
      month: t('churches.detail.calendarMonth'),
      year: t('churches.detail.calendarYear'),
      previousMonth: t('churches.detail.calendarPrevious'),
      nextMonth: t('churches.detail.calendarNext'),
    }),
    [t],
  );

  const existingOnTrial = subscription ? isTrialingSubscription(subscription) : false;
  const existingTrialPlan =
    subscription && existingOnTrial && isManualPaidPlan(subscription.plan)
      ? subscription.plan
      : null;
  const existingTrialEndsOn = subscription?.trialEndsAt
    ? isoToCalendarDate(subscription.trialEndsAt, timezone)
    : null;
  const existingPeriodEndsOn = subscription?.currentPeriodEnd
    ? isoToCalendarDate(subscription.currentPeriodEnd, timezone)
    : null;

  const subscriptionDirty =
    subscription &&
    (plan !== (existingOnTrial ? 'trial' : subscription.plan) ||
      trialPlan !== existingTrialPlan ||
      subscriptionStatus !== (existingOnTrial ? 'trialing' : subscription.status) ||
      Number(includedSeats) !== subscription.includedSeats ||
      trialEndsOn !== existingTrialEndsOn ||
      periodEndsOn !== existingPeriodEndsOn);

  const trialFieldsComplete = plan !== 'trial' || (Boolean(trialPlan) && Boolean(trialEndsOn));
  const periodFieldsComplete = !isManualPaidPlan(plan) || Boolean(periodEndsOn);

  const canSaveSubscription =
    Boolean(subscriptionDirty) &&
    subscriptionReason.trim().length >= 3 &&
    Boolean(includedSeats) &&
    Number(includedSeats) >= 1 &&
    trialFieldsComplete &&
    periodFieldsComplete;

  const canChangeStatus =
    isAdmin && church && (church.status === 'active' || church.status === 'suspended');

  const close = () => {
    onOpenChange(false);
    setPendingStatus(null);
    setStatusReason('');
    setPendingPayments(null);
    setPaymentsReason('');
    setSupportVisitOpen(false);
    setSupportVisitReason('');
    setSupportVisitLink(null);
    setSubscriptionReason('');
    setSubscriptionOpen(false);
    setCouponsOpen(false);
    setHistoryOpen(false);
  };

  const planSummary = (() => {
    if (!subscription) return '';
    if (existingOnTrial && isManualPaidPlan(subscription.plan)) {
      return t('churches.detail.trialingPlan', {
        plan: t(`billing.plans.${subscription.plan}`),
      });
    }
    if (existingOnTrial) return t('billing.plans.trial');
    return t(`billing.plans.${subscription.plan}`);
  })();

  const handlePlanChange = (next: Plan | null) => {
    if (!next) return;
    setPlan(next);
    if (next === 'trial') {
      setSubscriptionStatus('trialing');
      return;
    }
    if (plan === 'trial') {
      setSubscriptionStatus('active');
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent closeLabel={tCommon('action.close')} size="lg">
        <DialogHeader>
          <DialogTitle>{t('churches.detailTitle')}</DialogTitle>
          <DialogDescription className={church ? 'sr-only' : undefined}>
            {church
              ? `${church.name} — ${church.campusName}`
              : t('churches.detailDescription')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
          {detail.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="size-6 text-brand" />
            </div>
          ) : detail.isError || !church || !subscription ? (
            <p className="text-body-sm text-danger">{tErrors('UNEXPECTED')}</p>
          ) : (
            <div className="flex flex-col gap-6">
              {currentSession ? <PlatformSessionRequestNotice request={currentSession} /> : null}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-card-title font-semibold text-fg">{church.name}</p>
                  <p className="mt-0.5 text-body-sm text-fg-secondary">{church.campusName}</p>
                </div>
                <Badge variant={church.status === 'active' ? 'success' : 'neutral'}>
                  {t(`status.${church.status}`)}
                </Badge>
              </div>

              <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                <DetailField stacked label={t('churches.columns.plan')}>
                  {planSummary}
                </DetailField>
                <DetailField stacked label={t('churches.columns.subscription')}>
                  {t(`billing.statuses.${subscription.status}`)}
                </DetailField>
                <DetailField stacked label={t('churches.detail.seats')}>
                  {subscription.includedSeats}
                </DetailField>
                {church.country ? (
                  <DetailField stacked label={t('churches.detail.country')}>
                    {church.country}
                  </DetailField>
                ) : null}
                {subscription.trialEndsAt ? (
                  <DetailField stacked label={t('churches.detail.trialEnds')}>
                    {formatDate(subscription.trialEndsAt)}
                  </DetailField>
                ) : null}
                {subscription.currentPeriodEnd && !existingOnTrial ? (
                  <DetailField stacked label={t('churches.detail.periodEnds')}>
                    {formatDate(subscription.currentPeriodEnd)}
                  </DetailField>
                ) : null}
                {church.contactEmail ? (
                  <DetailField stacked label={t('churches.detail.contactEmail')}>
                    {church.contactEmail}
                  </DetailField>
                ) : null}
                <DetailField stacked label={t('churches.columns.created')}>
                  {formatDate(church.createdAt)}
                </DetailField>
                {payments?.suspended ? (
                  <DetailField stacked label={t('churches.paymentsPaused')}>
                    {payments.suspendedAt ? formatDate(payments.suspendedAt) : t('churches.paymentsPaused')}
                    {payments.suspendedReason
                      ? ` · ${t('churches.pausedReason', { reason: payments.suspendedReason })}`
                      : ''}
                  </DetailField>
                ) : null}
                {openTickets.data ? (
                  <DetailField stacked label={t('churches.detail.openSupportTickets')}>
                    {t('churches.detail.openSupportTicketsCount', {
                      count: openTickets.data.page.totalItems,
                    })}
                  </DetailField>
                ) : null}
              </dl>
            </div>
          )}
        </DialogBody>

        {church ? (
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(true)}>
              {t('churches.history')}
            </Button>
            {churchId && (openTickets.data?.page.totalItems ?? 0) > 0 ? (
              <Button variant="outline" asChild>
                <Link to={`/support/tickets?churchId=${churchId}`} onClick={() => onOpenChange(false)}>
                  {t('churches.detail.viewOpenTickets')}
                </Link>
              </Button>
            ) : null}
            {isAdmin ? (
              <>
                {currentSession ? null : (
                  <Button variant="outline" onClick={() => setSupportVisitOpen(true)}>
                    {t('churches.startSupportVisit')}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSubscriptionOpen(true)}>
                  {t('churches.changeSubscription')}
                </Button>
                <Button variant="outline" onClick={() => setCouponsOpen(true)}>
                  {t('churches.coupons')}
                </Button>
                {payments?.suspended ? (
                  <Button variant="outline" onClick={() => setPendingPayments('resume')}>
                    {t('churches.resumeGiving')}
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => setPendingPayments('suspend')}>
                    {t('churches.pauseGiving')}
                  </Button>
                )}
                {canChangeStatus && church.status === 'active' ? (
                  <Button variant="destructive" onClick={() => setPendingStatus('suspended')}>
                    {t('churches.suspend')}
                  </Button>
                ) : null}
                {canChangeStatus && church.status === 'suspended' ? (
                  <Button variant="outline" onClick={() => setPendingStatus('active')}>
                    {t('churches.reactivate')}
                  </Button>
                ) : null}
              </>
            ) : null}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>

    <Dialog
      open={pendingStatus !== null}
      onOpenChange={(next) => {
        if (!next) {
          setPendingStatus(null);
          setStatusReason('');
        }
      }}
    >
      <DialogContent closeLabel={t('common.cancel')} size="sm">
        <DialogHeader>
          <DialogTitle>
            {pendingStatus === 'suspended' ? t('churches.suspendTitle') : t('churches.reactivateTitle')}
          </DialogTitle>
          <DialogDescription>
            {pendingStatus === 'suspended'
              ? t('churches.suspendDescription', {
                  name: church ? `${church.name} — ${church.campusName}` : '',
                })
              : t('churches.reactivateDescription', {
                  name: church ? `${church.name} — ${church.campusName}` : '',
                })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
          <FormField label={t('churches.reason')} required>
            {(field) => (
              <Textarea
                {...field}
                value={statusReason}
                onChange={(event) => setStatusReason(event.target.value)}
                placeholder={t('churches.reasonPlaceholder')}
              />
            )}
          </FormField>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setPendingStatus(null);
              setStatusReason('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant={pendingStatus === 'suspended' ? 'destructive' : 'primary'}
            loading={updateStatus.isPending}
            disabled={statusReason.trim().length < 3}
            onClick={() => {
              if (!pendingStatus) return;
              updateStatus.mutate({
                status: pendingStatus,
                reason: statusReason.trim(),
              });
            }}
          >
            {pendingStatus === 'suspended'
              ? t('churches.confirmSuspend')
              : t('churches.confirmReactivate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      open={subscriptionOpen}
      onOpenChange={(next) => {
        if (!next) {
          setSubscriptionOpen(false);
          setSubscriptionReason('');
        }
      }}
    >
      <DialogContent closeLabel={t('common.cancel')} size="md">
        <DialogHeader>
          <DialogTitle>{t('churches.subscriptionDialog.title')}</DialogTitle>
          <DialogDescription>{t('churches.subscriptionDialog.description')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
          <FormField label={t('churches.columns.plan')}>
            {(field) => (
              <Listbox
                {...field}
                popoverPortal="dialog"
                value={plan}
                onChange={handlePlanChange}
                options={planOptions}
              />
            )}
          </FormField>
          {plan === 'trial' ? (
            <FormField
              label={t('churches.detail.trialPlan')}
              required
              hint={t('churches.detail.trialPlanHint')}
            >
              {(field) => (
                <Listbox
                  {...field}
                  popoverPortal="dialog"
                  value={trialPlan}
                  onChange={setTrialPlan}
                  options={trialPlanOptions}
                  placeholder={t('churches.detail.trialPlanPlaceholder')}
                />
              )}
            </FormField>
          ) : (
            <FormField label={t('churches.columns.subscription')}>
              {(field) => (
                <Listbox
                  {...field}
                  popoverPortal="dialog"
                  value={subscriptionStatus}
                  onChange={(value) => value && setSubscriptionStatus(value)}
                  options={subscriptionStatusOptions}
                />
              )}
            </FormField>
          )}
          {plan === 'trial' ? (
            <FormField
              label={t('churches.detail.trialEnds')}
              required
              hint={t('churches.detail.trialEndsHint')}
            >
              {(field) => (
                <DatePicker
                  id={field.id}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                  value={trialEndsOn}
                  onChange={setTrialEndsOn}
                  placeholder={t('churches.detail.trialEndsPlaceholder')}
                  popoverPortal="dialog"
                  calendarLabels={calendarLabels}
                />
              )}
            </FormField>
          ) : isManualPaidPlan(plan) ? (
            <FormField
              label={t('churches.detail.periodEnds')}
              required
              hint={t('churches.detail.periodEndsHint')}
            >
              {(field) => (
                <DatePicker
                  id={field.id}
                  aria-describedby={field['aria-describedby']}
                  invalid={field.invalid}
                  value={periodEndsOn}
                  onChange={setPeriodEndsOn}
                  placeholder={t('churches.detail.periodEndsPlaceholder')}
                  popoverPortal="dialog"
                  calendarLabels={calendarLabels}
                />
              )}
            </FormField>
          ) : null}
          <FormField label={t('churches.detail.seats')}>
            {(field) => (
              <Input
                {...field}
                type="number"
                min={1}
                max={500}
                value={includedSeats}
                onChange={(event) => setIncludedSeats(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            label={t('churches.reason')}
            required
            hint={t('churches.detail.subscriptionReasonHint')}
          >
            {(field) => (
              <Textarea
                {...field}
                value={subscriptionReason}
                onChange={(event) => setSubscriptionReason(event.target.value)}
                placeholder={t('churches.reasonPlaceholder')}
              />
            )}
          </FormField>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setSubscriptionOpen(false);
              setSubscriptionReason('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            loading={updateSubscription.isPending}
            disabled={!canSaveSubscription}
            onClick={() =>
              updateSubscription.mutate({
                plan,
                status: subscriptionStatus,
                includedSeats: Number(includedSeats),
                reason: subscriptionReason.trim(),
                trialPlan: trialPlan ?? undefined,
                trialEndsAt: trialEndsOn,
                currentPeriodEnd: periodEndsOn,
              })
            }
          >
            {t('churches.saveSubscription')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      open={pendingPayments !== null}
      onOpenChange={(next) => {
        if (!next) {
          setPendingPayments(null);
          setPaymentsReason('');
        }
      }}
    >
      <DialogContent closeLabel={t('common.cancel')} size="sm">
        <DialogHeader>
          <DialogTitle>
            {pendingPayments === 'suspend' ? t('churches.pauseGivingTitle') : t('churches.resumeGivingTitle')}
          </DialogTitle>
          <DialogDescription>
            {pendingPayments === 'suspend'
              ? t('churches.pauseGivingDescription', {
                  name: church ? `${church.name} — ${church.campusName}` : '',
                })
              : t('churches.resumeGivingDescription', {
                  name: church ? `${church.name} — ${church.campusName}` : '',
                })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
          <FormField label={t('churches.reason')} required>
            {(field) => (
              <Textarea
                {...field}
                value={paymentsReason}
                onChange={(event) => setPaymentsReason(event.target.value)}
                placeholder={t('churches.reasonPlaceholder')}
              />
            )}
          </FormField>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setPendingPayments(null);
              setPaymentsReason('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant={pendingPayments === 'suspend' ? 'destructive' : 'primary'}
            loading={updatePayments.isPending}
            disabled={paymentsReason.trim().length < 3}
            onClick={() => {
              if (!pendingPayments) return;
              updatePayments.mutate({
                suspended: pendingPayments === 'suspend',
                reason: paymentsReason.trim(),
              });
            }}
          >
            {pendingPayments === 'suspend'
              ? t('churches.confirmPauseGiving')
              : t('churches.confirmResumeGiving')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      open={supportVisitOpen}
      onOpenChange={(next) => {
        if (!next) {
          setSupportVisitOpen(false);
          setSupportVisitReason('');
          setSupportVisitLink(null);
        }
      }}
    >
      <DialogContent closeLabel={t('common.cancel')} size="sm">
        <DialogHeader>
          <DialogTitle>{t('churches.supportVisitTitle')}</DialogTitle>
          <DialogDescription>
            {supportVisitLink
              ? t('churches.supportVisitLinkHint')
              : t('churches.supportVisitDescription', {
                  name: church ? `${church.name} — ${church.campusName}` : '',
                })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
          {supportVisitLink ? (
            <p className="break-all text-body-sm text-fg-secondary">{supportVisitLink}</p>
          ) : (
            <FormField label={t('churches.supportVisitReason')} required>
              {(field) => (
                <Textarea
                  {...field}
                  value={supportVisitReason}
                  onChange={(event) => setSupportVisitReason(event.target.value)}
                  placeholder={t('churches.supportVisitReasonPlaceholder')}
                />
              )}
            </FormField>
          )}
        </DialogBody>
        <DialogFooter>
          {supportVisitLink ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(supportVisitLink).then(
                    () => toast.success(t('churches.supportVisitCopied')),
                    () => toast.error(t('churches.supportVisitCopyFailed')),
                  );
                }}
              >
                {t('churches.supportVisitCopy')}
              </Button>
              <Button onClick={() => window.open(supportVisitLink, '_blank', 'noopener')}>
                {t('churches.supportVisitOpen')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setSupportVisitOpen(false);
                  setSupportVisitReason('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                loading={startSupportVisit.isPending}
                disabled={supportVisitReason.trim().length < 3}
                onClick={() => startSupportVisit.mutate(supportVisitReason.trim())}
              >
                {t('churches.supportVisitConfirm')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ChurchHistoryDialog
      churchId={churchId}
      churchName={church ? `${church.name} — ${church.campusName}` : ''}
      open={historyOpen}
      onOpenChange={setHistoryOpen}
    />
    <ChurchCouponsDialog
      churchId={churchId}
      churchName={church ? `${church.name} — ${church.campusName}` : ''}
      open={couponsOpen}
      onOpenChange={setCouponsOpen}
    />
  </>
  );
};
